import type { IConfirmedTissue } from '@/types/confirmedGrafts';
import type { IUnassignedTissue } from '@/types/graftsToAssigned';
import type { Patients } from '@/types/patient';

/**
 * Creates mock Patients data for the GraftLogs tutorial
 * @param index - The index of the mock Patient to create (0-9)
 * @returns A mock Patients object
 */
export const createMockPatient = (index: number): Patients => {
	const baseDate = new Date('2025-01-15');
	const birthDate = new Date(baseDate);
	birthDate.setFullYear(birthDate.getFullYear() - (30 + index * 5));

	// Patient name variations - more realistic names
	const firstNames = [
		'Michael',
		'Patricia',
		'Elena',
		'Jelan',
		'Andrew',
		'David',
		'Carlos',
		'David',
		'Jennifer',
		'Robert',
	];
	const lastNames = [
		'Fred',
		'Test',
		'Dev',
		'Warner',
		'Wassanberg',
		'Smith',
		'Smith',
		'Parker',
		'Williams',
		'Brown',
	];

	// Clinic variations
	const clinics = [
		{ id: '61aea445-e3b4-ea11-a816-000d3a191041', name: 'A Podiatry Clinic' },
		{ id: '72bfb556-f4c5-fb22-b927-000d3a191042', name: 'Dev B Clinic' },
		{ id: '83cgc667-g5d6-gc33-ca38-000d3a191043', name: 'Dev A Clinic' },
		{ id: '94dhd778-h6e7-hd44-db49-000d3a191044', name: 'Dev E Clinic' },
		{
			id: 'a5eie889-i7f8-ie55-ec5a-000d3a191045',
			name: 'Podiatry Care Clinic',
		},
	];

	// Insurance variations with codes in parentheses
	const insurances = [
		{ id: 'ins-001', name: 'Aetna American Continental Insurance (30074)' },
		{ id: 'ins-002', name: 'American Family Insurance (NRT/56071)' },
		{ id: 'ins-003', name: 'Medicare (Part B)' },
		{ id: 'ins-004', name: '1199 National Benefit Fund (SNATB/13162)' },
		{ id: 'ins-005', name: 'Blue Cross Blue Shield (BCBS/12345)' },
	];

	const firstName = firstNames[index % firstNames.length];
	const lastName = lastNames[index % lastNames.length];
	const clinic = clinics[index % clinics.length];
	const insurance = insurances[index % insurances.length];
	const contactId = crypto.randomUUID();

	// EMR ID variations - some with "EMR-" prefix, some numeric only, some with "-"
	const emrIdVariations = [
		`EMR-${String(10000 + index).padStart(5, '0')}`,
		String(11890234 + index),
		String(23462208823 + index),
		'-',
		String(217726 + index),
	];
	const emrId = emrIdVariations[index % emrIdVariations.length];

	return {
		fullname: `${firstName} ${lastName}`,
		firstname: firstName,
		lastname: lastName,
		// Primary Member ID variations - different formats
		ta_primarymemberid:
			index % 4 === 0
				? `PR-${String(12345 + index).padStart(5, '0')}`
				: index % 4 === 1
					? `CIG${String(772190 + index)}`
					: index % 4 === 2
						? String(12345 + index)
						: String(234324 + index),
		ta_secondarymemberid:
			index % 2 === 0
				? `MEM-${String(6000 + index).padStart(6, '0')}`
				: undefined,
		mobilephone: `555-${String(1000 + index).padStart(4, '0')}`,
		emailaddress1: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
		createdon: new Date(
			baseDate.getTime() - index * 30 * 24 * 60 * 60 * 1000,
		).toISOString(),
		contactid: contactId,
		birthdate: birthDate.toISOString().split('T')[0],
		statuscode: '1',
		ta_emrid: emrId,
		gendercode: index % 2 === 0 ? '1' : '2', // 1 = Male, 2 = Female
		preferredcontactmethodcode: '1',
		address1_line1: `${100 + index} Main Street`,
		address1_city: 'New York',
		address1_stateorprovince: 'NY',
		address1_postalcode: `${10000 + index}`,
		customertypecode: '1',
		ta_originationsource: '0',
		primaryInsurance: {
			ta_insuranceid: insurance.id,
			ta_name: insurance.name,
		},
		secondaryInsurance:
			index % 3 === 0
				? {
						ta_insuranceid: 'ins-006',
						ta_name: 'Medicaid',
					}
				: null,
		account: {
			accountid: clinic.id,
			name: clinic.name,
			statuscode: 1,
			emailaddress1: 'clinic@example.com',
			ta_originationsource: 0,
			modifiedon: new Date().toISOString(),
			createdon: new Date().toISOString(),
			ta_qbocustomerid: '',
			ta_accountstatus: 953870001,
		},
		parentcustomerid_account: clinic.id,
	};
};

/**
 * Creates mock IUnassignedTissue data for the GraftLogs tutorial
 * @param index - The index of the mock UnassignedTissue to create (0-9)
 * @returns A mock IUnassignedTissue object
 */
export const createMockUnassignedTissue = (
	index: number,
): IUnassignedTissue => {
	const baseDate = new Date('2025-01-15');
	const procedureDate = new Date(baseDate);
	procedureDate.setDate(procedureDate.getDate() - index * 5);

	// Product variations
	const products = [
		'Axolotl Dual Graft (Q4332)',
		'Amnio-Maxx (Q4239)',
		'Helicoll (Q4164)',
		'DermaMatrix (Q4123)',
		'AlloDerm (Q4056)',
	];

	// Size variations
	const sizes = ['2x2', '3x3', '4x4', '5x5', '2x4'];

	// Physician variations
	const physicians = [
		'Edward Jenner',
		'Sarah Johnson',
		'Michael Chen',
		'Emily Rodriguez',
		'David Thompson',
	];

	// Patient variations
	const patients = [
		'Cecil Ewing',
		'Ronald Lyon',
		'Carl Moore',
		'Micky Leach',
		'Bonnie Wymbs',
		'Robert Stever',
		'Bobby Harris',
		'Barbara Denson',
		'Maxine Singleton',
		'Riyan Irfan',
	];

	// Clinic variations
	const clinics = [
		'A Podiatry Clinic',
		'Podiatry Care Clinic',
		'Advanced Foot & Ankle',
		'Coastal Podiatry Group',
		'Metro Foot Specialists',
	];

	// Order status variations
	const orderStatuses = [
		'Pending',
		'Shipped',
		'In Transit',
		'Delivered',
		'Processing',
	];

	const product = products[index % products.length];
	const size = sizes[index % sizes.length];
	const physician = physicians[index % physicians.length];
	const patient = patients[index % patients.length];
	const clinic = clinics[index % clinics.length];
	const orderStatus = orderStatuses[index % orderStatuses.length];
	// Tissue ID variations - different formats like in QA
	const tissueIdVariations = [
		`PTT-22-${String(1964 + index).padStart(4, '0')}-${String(3 + index).padStart(4, '0')}`,
		`${String(34009 + index)}-LYA-${String(220427 + index)}`,
		`BLS20-${String(68 + index).padStart(4, '0')}-${String(151 + index).padStart(3, '0')}`,
		`BT-${123 + index}-${123 + index}-${123 + index}`,
		`TID-${String(6546471 + index)}`,
	];
	const tissueId = tissueIdVariations[index % tissueIdVariations.length];
	const emrId = String(217726 + index);
	const inventoryRowId = `inv-${String(2000 + index).padStart(6, '0')}`;
	const dynamicsId = `DYN-${String(3000 + index).padStart(6, '0')}`;
	const orderRowId = `ord-${String(4000 + index).padStart(6, '0')}`;

	return {
		inventoryrowid1: inventoryRowId,
		DynamicsID: dynamicsId,
		physician: physician,
		clinic: clinic,
		product: product,
		size: size,
		proceduredate: procedureDate.toISOString().split('T')[0],
		emrid: emrId,
		orderstatus: orderStatus,
		patient: patient,
		orderrowid: orderRowId,
		tissueid: tissueId,
		creditmemo:
			index % 4 === 0 ? `CM-${String(5000 + index).padStart(4, '0')}` : null,
	};
};

/**
 * Creates mock IConfirmedTissue data for the GraftLogs tutorial
 * @param index - The index of the mock ConfirmedTissue to create (0-9)
 * @returns A mock IConfirmedTissue object
 */
export const createMockConfirmedTissue = (index: number): IConfirmedTissue => {
	const baseDate = new Date('2025-01-15');
	const serviceDate = new Date(baseDate);
	serviceDate.setDate(serviceDate.getDate() - index * 7);

	const shippedDate =
		index % 2 === 0
			? new Date(serviceDate.getTime() + 3 * 24 * 60 * 60 * 1000)
			: null;
	const createDate = new Date(serviceDate.getTime() - 2 * 24 * 60 * 60 * 1000);
	const modifyDate =
		index % 3 === 0
			? new Date(serviceDate.getTime() - 1 * 24 * 60 * 60 * 1000)
			: null;

	// Product variations - some with Q codes, some without
	const products = [
		'Axolotl Dual Graft',
		'Biovance',
		'Esano AC',
		'Microlyte (A2005)',
		'Helicoll (Q4164)',
	];

	// Size variations - more descriptive formats
	const sizes = [
		'Dual Graft 1x2cm',
		'4 x 4cm',
		'2 x 2cm',
		'2 x 4cm',
		'Dual Graft 4x6cm',
	];

	// Physician variations - some with "Dr.", some without
	const physicians = [
		'Alexander Flemming',
		'Edward Jenner',
		'Robert Koch',
		'Louis Pasteur',
		'Joseph Lister',
	];

	// Patient variations
	const patients = [
		'Andrew Simon',
		'Kenneth Singlton',
		'Becky Wassanberg',
		'Mikki Bishop',
		'William Reed',
		'Sarah Johnson',
		'Robert Brown',
		'Maria Garcia',
		'David Martinez',
		'Jennifer Wilson',
	];

	// Clinic variations
	const clinics = [
		'A Podiatry Clinic',
		'Podiatry Care Clinic',
		'Advanced Foot & Ankle',
		'Coastal Podiatry Group',
		'Metro Foot Specialists',
	];

	const product = products[index % products.length];
	const size = sizes[index % sizes.length];
	const physician = physicians[index % physicians.length];
	const patient = patients[index % patients.length];
	const clinic = clinics[index % clinics.length];
	// Tissue ID variations - different formats like in QA
	const tissueIdVariations = [
		`TID-${String(6546471 + index)}`,
		`TID-${String(46871346 + index)}`,
		`BLS342-${String(123123123 + index)}`,
		String(456 + index),
		`PTT-22-${String(1964 + index).padStart(4, '0')}-${String(3 + index).padStart(4, '0')}`,
	];
	const tissueId = tissueIdVariations[index % tissueIdVariations.length];
	const emrId = String(217726 + index);
	const graftLogsId = 1000 + index;
	const trackingNumber = shippedDate ? `1Z999AA1012345678${index}` : null;
	const inventoryRowId = index % 2 === 0 ? 2000 + index : null;
	const createUser = 'System Admin';
	const modifyUser = modifyDate ? 'User Admin' : null;

	return {
		GraftLogsID: graftLogsId,
		GraftLogsType: 'Graftlogs',
		Clinic: clinic,
		Product: product,
		Physician: physician,
		TissueID: tissueId,
		Patient: patient,
		DateofService: serviceDate.toISOString().split('T')[0],
		DateShippedBack: shippedDate
			? shippedDate.toISOString().split('T')[0]
			: null,
		TrackingNumber: trackingNumber,
		CreateDate: createDate.toISOString(),
		CreateUser: createUser,
		ModifyDate: modifyDate ? modifyDate.toISOString() : null,
		ModifyUser: modifyUser,
		EMRID: emrId,
		AttachedImage: index % 3 === 0 ? `image-${index}.jpg` : null,
		Size: size,
		InventoryRowid1: inventoryRowId,
		CMemoRMA:
			index % 4 === 0 ? `CM-${String(5000 + index).padStart(4, '0')}` : null,
		Type: 'Graftlogs',
	};
};

/**
 * Generates an array of mock Patients data for the tutorial
 * @param count - Number of mock Patients to generate (default: 10)
 * @returns Array of mock Patients objects
 */
export const generateMockPatientsData = (count = 10): Patients[] => {
	return Array.from({ length: count }, (_, index) => createMockPatient(index));
};

/**
 * Generates an array of mock IUnassignedTissue data for the tutorial
 * @param count - Number of mock UnassignedTissue to generate (default: 10)
 * @returns Array of mock IUnassignedTissue objects
 */
export const generateMockUnassignedTissueData = (
	count = 10,
): IUnassignedTissue[] => {
	return Array.from({ length: count }, (_, index) =>
		createMockUnassignedTissue(index),
	);
};

/**
 * Generates an array of mock IConfirmedTissue data for the tutorial
 * @param count - Number of mock ConfirmedTissue to generate (default: 10)
 * @returns Array of mock IConfirmedTissue objects
 */
export const generateMockConfirmedTissueData = (
	count = 10,
): IConfirmedTissue[] => {
	return Array.from({ length: count }, (_, index) =>
		createMockConfirmedTissue(index),
	);
};
